'use client'

import { Suspense, useEffect, useState } from 'react';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { appStorage } from '../utils/appStorage';
import { authManager } from '../utils/authManager';
import { cookieManager, CookieName } from '../utils/cookieManager';
import Dialog, { DialogButton } from './ui/dialog';
import logger from '../utils/logger';

const VERSION_KEY = 'app_version';
const CACHE_VERSION_KEY = 'app-version-cache';

/**
 * バージョンチェックの内部コンポーネント
 * useSearchParamsを使用するため、Suspenseでラップが必要
 */
function VersionCheckWrapperInner({ children }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [showModal, setShowModal] = useState(false);
  const [forceMode, setForceMode] = useState(false); // URLパラメータで強制表示
  const [versionInfo, setVersionInfo] = useState({
    current: '',
    localStorage: '',
    sessionStorage: '',
    cookie: '',
    cache: '',
    outdated: []
  });

  // バージョン取得ヘルパー
  const getVersions = async () => {
    // サーバーから最新バージョンを取得（PWAキャッシュされたJSのインライン値に依存しない）
    let current = process.env.NEXT_PUBLIC_APP_VERSION || '';
    try {
      const res = await fetch('/api/version', { cache: 'no-store' });
      if (res.ok) {
        const data = await res.json();
        if (data.version) current = data.version;
      }
    } catch (e) {
      logger.warn('Failed to fetch server version, using bundled version:', e);
    }
    const localVer = localStorage.getItem(VERSION_KEY) || '';
    const sessionVer = sessionStorage.getItem(VERSION_KEY) || '';
    const cookieVer = getCookieVersion();
    const cacheVer = await getCacheVersion();

    return { current, localVer, sessionVer, cookieVer, cacheVer };
  };

  // Cookieからバージョン取得
  const getCookieVersion = () => {
    const match = document.cookie.match(new RegExp(`(^| )${VERSION_KEY}=([^;]+)`));
    return match ? match[2] : '';
  };

  // Cookieにバージョン保存
  const setCookieVersion = (version) => {
    document.cookie = `${VERSION_KEY}=${version};path=/;max-age=${60 * 60 * 24 * 365}`;
  };

  // Service Workerキャッシュからバージョン取得
  const getCacheVersion = async () => {
    if (!('caches' in window)) return '';
    try {
      const cache = await caches.open(CACHE_VERSION_KEY);
      const response = await cache.match('/version');
      if (response) {
        return await response.text();
      }
    } catch (e) {
      logger.error('Cache version read error:', e);
    }
    return '';
  };

  // Service Workerキャッシュにバージョン保存
  const setCacheVersion = async (version) => {
    if (!('caches' in window)) return;
    try {
      const cache = await caches.open(CACHE_VERSION_KEY);
      await cache.put('/version', new Response(version));
    } catch (e) {
      logger.error('Cache version write error:', e);
    }
  };

  // 全ストレージにバージョン保存
  const saveVersionToAll = async (version) => {
    localStorage.setItem(VERSION_KEY, version);
    sessionStorage.setItem(VERSION_KEY, version);
    setCookieVersion(version);
    await setCacheVersion(version);
  };

  // アプリ名に応じてブラウザタブのタイトルを設定（マウント時 + ページ遷移時）
  useEffect(() => {
    const setTitle = () => {
      const appTitle = appStorage.getAppTitle();
      if (appTitle) {
        document.title = appTitle.replace(/ /g, '') + 'アプリ';
      }
    };
    setTitle();
    // sessionStorageへの書き込みが遅延する場合に備えて再試行
    const timer = setTimeout(setTitle, 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const appTitle = appStorage.getAppTitle();
    if (appTitle) {
      document.title = appTitle.replace(/ /g, '') + 'アプリ';
    }
  }, [pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // /debugページではバージョンチェックをスキップ
    if (pathname === '/debug') return;

    // ============================================================
    // [2026-03-16 無効化] ?clear_storage=1 によるデータ削除機能
    // 経緯: この機能を使用すると、IndexedDB削除時にブラウザが
    //       フリーズする問題が報告された（Redmine #530）。
    //       原因調査・修正が完了するまでコメントアウトで無効化する。
    //       通常のバージョン更新時のログアウト機能には影響しない。
    // 注意: このコメントアウトを解除する前に #530 の対応を完了すること。
    // ============================================================
    // // URLパラメータで強制表示 (?clear_storage=1)
    // const forceClear = searchParams.get('clear_storage');
    // if (forceClear === '1') {
    // const current = process.env.NEXT_PUBLIC_APP_VERSION || 'N/A';
    // setVersionInfo({
    // current,
    // localStorage: '',
    // sessionStorage: '',
    // cookie: '',
    // cache: '',
    // outdated: ['URLパラメータによる強制削除モード'],
    // previousVersion: ''
    // });
    // setForceMode(true);
    // setShowModal(true);
    // return;
    // }

    const checkVersions = async () => {
      try {
        const { current, localVer, sessionVer, cookieVer, cacheVer } = await getVersions();

        logger.log('=== VersionCheckWrapper Debug ===');
        logger.log('Current app version (env):', current);
        logger.log('localStorage version:', localVer || '(empty)');
        logger.log('sessionStorage version:', sessionVer || '(empty)');
        logger.log('Cookie version:', cookieVer || '(empty)');
        logger.log('Cache version:', cacheVer || '(empty)');

        if (!current) {
          logger.warn('NEXT_PUBLIC_APP_VERSION is not defined');
          return;
        }

        // いずれかのストレージに保存されている以前のバージョンを取得（sessionStorageは除外）
        const previousVersion = localVer || cookieVer || cacheVer;

        // 完全な初回アクセス（sessionStorage以外のストレージにバージョンがない）
        if (!previousVersion) {
          await saveVersionToAll(current);
          logger.log('>>> First visit, version synced to all storages:', current);
          logger.log('=================================');
          return;
        }

        // バージョンが変更されたかチェック
        const versionChanged = previousVersion !== current;

        // 一部のストレージにバージョンがない（不完全な状態）- sessionStorageは除外（タブを閉じると消えるため）
        const hasAnyVersion = localVer || cookieVer || cacheVer;
        const missingInSome = hasAnyVersion && (!localVer || !cookieVer || !cacheVer);

        logger.log('Previous version:', previousVersion);
        logger.log('Version changed:', versionChanged);
        logger.log('Missing in some storages:', missingInSome);
        logger.log('=================================');

        if (versionChanged || missingInSome) {
          logger.log('>>> Showing version update modal');

          const outdated = [];
          if (localVer && localVer !== current) outdated.push(`localStorage: ${localVer}`);
          else if (!localVer) outdated.push('localStorage: (未設定)');

          if (sessionVer && sessionVer !== current) outdated.push(`sessionStorage: ${sessionVer}`);
          else if (!sessionVer) outdated.push('sessionStorage: (未設定)');

          if (cookieVer && cookieVer !== current) outdated.push(`Cookie: ${cookieVer}`);
          else if (!cookieVer) outdated.push('Cookie: (未設定)');

          if (cacheVer && cacheVer !== current) outdated.push(`Cache: ${cacheVer}`);
          else if (!cacheVer) outdated.push('Cache: (未設定)');

          setVersionInfo({
            current,
            localStorage: localVer,
            sessionStorage: sessionVer,
            cookie: cookieVer,
            cache: cacheVer,
            outdated,
            previousVersion
          });
          setShowModal(true);
        } else {
          // 全て最新の場合、念のため全ストレージにバージョン保存
          await saveVersionToAll(current);
          logger.log('>>> All storages up to date:', current);
        }
      } catch (error) {
        logger.error('Error in VersionCheckWrapper:', error);
      }
    };

    checkVersions();
  }, [searchParams, pathname]);

  // データ削除処理
  const handleClearData = async () => {
    try {
      // メールアドレスとアプリ名を先に取得して保持
      const savedEmail = cookieManager.getCookie(CookieName.UserEmail) || localStorage.getItem('saved_user_email') || '';
      const savedAppName = appStorage.getAppName() || searchParams.get('app') || 'smartenglish';

      // LocalStorage完全クリア
      localStorage.clear();

      // SessionStorageクリア
      sessionStorage.clear();

      // Cookie完全クリア（device_idは保持）
      document.cookie.split(';').forEach(cookie => {
        const name = cookie.split('=')[0].trim();
        if (name === 'device_id') return;
        document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
      });

      // Service Workerキャッシュ削除（VOSKモデルのキャッシュは保持）
      // VOSKの音声認識モデル（約40MB）はブラウザのHTTPキャッシュに保存されるため、
      // バージョン更新時に全キャッシュを削除するとモデルの再ダウンロードが必要になり
      // 準備に1分以上かかる。バージョン管理用キャッシュのみ削除する。
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        const versionCacheOnly = cacheNames.filter(name => name === 'app-version-cache');
        await Promise.all(versionCacheOnly.map(name => caches.delete(name)));
      }

      // IndexedDB削除（VOSK音声認識モデル関連のDBは保持）
      // vosk-browserがIndexedDBにモデルデータをキャッシュしている可能性があるため除外
      if ('indexedDB' in window) {
        try {
          const databases = await window.indexedDB.databases();
          // VOSK関連のDBは除外（音声認識モデルのキャッシュを保持するため）
          const nonVoskDbs = databases.filter(db => !db.name?.toLowerCase().includes('vosk'));
          await Promise.all(nonVoskDbs.map(db => {
            return new Promise((resolve, reject) => {
              const req = window.indexedDB.deleteDatabase(db.name);
              req.onsuccess = resolve;
              req.onerror = reject;
              req.onblocked = resolve;
            });
          }));
        } catch (e) {
          logger.error('IndexedDB clear error:', e);
        }
      }

      // authManagerでも削除
      authManager.clearAllData();

      // 新しいバージョンを全ストレージに保存
      await saveVersionToAll(versionInfo.current);

      setShowModal(false);

      // メールアドレスとアプリ名を引き継いでログインページへリダイレクト
      const params = new URLSearchParams();
      params.set('app', savedAppName);
      params.set('reason', 'version_updated');
      if (savedEmail) {
        params.set('email', savedEmail);
      }
      router.replace(`/login?${params.toString()}`);
    } catch (error) {
      logger.error('Error clearing data:', error);
      location.reload();
    }
  };

  // 古いバージョン表示用（outdated配列をそのまま使用）
  const getOldVersionDisplay = () => {
    return versionInfo.outdated || [];
  };

  return (
    <>
      {children}
      <Dialog
        isOpen={showModal}
        onClose={() => {}}
        title={forceMode ? "データ削除" : "アプリがバージョンアップされました"}
      >
        <div className="text-left text-sm mb-4">
          {forceMode ? (
            <>
              <p className="mb-2">保存されているデータを削除しますか？</p>
              <p className="text-xs text-gray-600 mb-2">
                Cookie、localStorage、sessionStorage、Service Workerキャッシュを削除します。
              </p>
            </>
          ) : (
            <>
              <p className="mb-2">正常に動作させるために以下のボタンから、ログアウトしてください。</p>
              {versionInfo.previousVersion && versionInfo.previousVersion !== versionInfo.current && (
                <p className="mb-1 text-xs text-gray-600">以前のバージョン: <span className="font-bold">{versionInfo.previousVersion}</span></p>
              )}
              <p className="text-xs text-gray-600 mb-2">新バージョン: {versionInfo.current}</p>
            </>
          )}
        </div>
        <DialogButton onClick={handleClearData} variant="primary">
          {forceMode ? 'データを削除' : 'ログアウト'}
        </DialogButton>
      </Dialog>
    </>
  );
}

/**
 * バージョンチェックラッパーコンポーネント
 *
 * localStorage, sessionStorage, Cookie, Service Workerキャッシュ
 * それぞれにバージョン情報を保存し、どれか一つでも古ければモーダル表示
 */
export default function VersionCheckWrapper({ children }) {
  return (
    <Suspense fallback={<>{children}</>}>
      <VersionCheckWrapperInner>{children}</VersionCheckWrapperInner>
    </Suspense>
  );
}
